import { useReducer, useState, useCallback } from 'react';

import { EditorState } from 'draft-js';
import Task1 from 'models/task1';

import {
  getMentionCount,
  getAllMentionEntities,
  getApiBodyFromEditorStateAndMedia,
  deleteAllMentionEntities,
} from 'services/helpers/post';

const initialState = new Task1({});
export const UPDATE_DUE_DATE = 'UPDATE_DATE';
export const UPDATE_TO_IDS = 'UPDATE_TO_IDS';
export const UPDATE_EDITOR_STATE = 'UPDATE_EDITOR_STATE';
export const UPDATE_TASK = 'UPDATE_TASK';
export const UPDATE_TEAM_ID = 'UPDATE_TEAM';
export const UPDATE_COMPLETION_DATE = 'UPDATE_COMPLETION_DATE';
export const ADD_TO = 'ADD_TO';
export const REMOVE_TO = 'REMOVE_TO';
export const UPDATE_COMPANY_GOAL = 'UPDATE_COMPANY_GOAL';
export const UPDATE_DESCRIPTION = 'UPDATE_DESCRIPTION';
export const CLEAR_TASK = 'CLEAR_TASK';

const initialEditorState = EditorState.createEmpty();
export function editorStateReducer(state = initialEditorState, action) {
  switch (action.type) {
    case UPDATE_EDITOR_STATE: {
      return { ...state, editorState: action.editorState };
    }
    default:
      return state;
  }
}

export function updateToIdsOnEmployeeAdd({ toIds, editorState, toAddEmployee }) {
  let updatedToIds = [...toIds];
  const index = toIds.findIndex((employee) => employee.id === toAddEmployee.id);
  if (index === -1) {
    const mentions = getMentionCount(editorState);
    // this is the only mention that exists, so we need to override toIds and just insert the current one
    if (mentions === 0) updatedToIds = [];
    updatedToIds = [...updatedToIds, { ...toAddEmployee }];
  }
  return updatedToIds;
}

// if the employee doesn't exist then only the employee would be added
export function addEmployeeToTask(task, toAddEmployee) {
  const updatedToIds = updateToIdsOnEmployeeAdd({
    toIds: task.toIds,
    editorState: task.editorState,
    toAddEmployee,
  });

  return { ...task, toIds: updatedToIds };
}

export function currentTaskReducer(state, action) {
  switch (action.type) {
    case UPDATE_TASK: {
      // console.log('normalized task', normalize(action.task, taskSchema));

      return { ...state, ...action.task };
    }
    case UPDATE_EDITOR_STATE: {
      return { ...state, editorState: action.editorState };
    }
    case UPDATE_TO_IDS:
      // since toIds will contain employees and it will be deep nesting
      // make sure that it doesn't duplicate
      const toIds = action.toIds || [];
      return { ...state, toIds: [...toIds] };
    case UPDATE_DUE_DATE:
      return { ...state, dueDate: action.dueDate };
    // See what should happen about updating teamName
    case UPDATE_TEAM_ID:
      return { ...state, teamId: action.teamId };
    case UPDATE_COMPLETION_DATE:
      return { ...state, completionDate: action.completionDate };
    case ADD_TO: {
      return addEmployeeToTask(state, action.to);
    }
    case UPDATE_DESCRIPTION: {
      return { ...state, description: action.description };
    }
    case REMOVE_TO: {
      const newState = { ...state };
      const toIds = state.toIds.filter((employee) => employee.id !== action.toId);
      newState.toIds = toIds;
      return newState;
    }
    case UPDATE_COMPANY_GOAL: {
      return { ...state, companyGoal: action.companyGoal };
    }
    case CLEAR_TASK: {
      return {};
    }

    default:
      return state;
  }
}

export const updateTextMacros = (task) => {
  const newTask = { ...task };
  const { text, macros } = getApiBodyFromEditorStateAndMedia(newTask.editorState);
  newTask.text = text;
  newTask.macros = macros;
  return newTask;
};

export const addTaggedEmployeesToOwners = (task) => {
  const toIds = [...task.toIds];
  const keysMap = {};
  toIds.forEach((toId) => (keysMap[toId.id] = true));
  const mentions = getAllMentionEntities(task.editorState);
  mentions.forEach(({ entityData }) => {
    const { employee } = entityData.mention;
    if (!toIds[employee.id]) toIds.push(employee);
  });
  return { ...task, toIds };
};

export const deleteMentions = (task) => {
  const newEditorState = deleteAllMentionEntities(task.editorState);
  return { ...task, editorState: newEditorState };
};

export const addTaggedEmployeesUpdateMacrosDeleteMentions = (task) =>
  updateTextMacros(deleteMentions(addTaggedEmployeesToOwners(task)));

// TODO: you should combine the above two reducers and then use it

export default function useTaskReducer(task = {}) {
  const [currentTask, dispatch] = useReducer(currentTaskReducer, { ...task });
  // use it when you want to be able to have a functionality like onEsc where you want that particular field to reset its value
  // Make sure the keys here match the keys of a task model
  const [restorableData, setRestorableData] = useState({});
  const updateDueDate = (dueDate) => {
    dispatch({ type: UPDATE_DUE_DATE, dueDate });
  };

  const updateTeamId = (teamId) => {
    dispatch({ type: UPDATE_TEAM_ID, teamId });
  };

  const updateToIds = useCallback((toIds = []) => {
    dispatch({ type: UPDATE_TO_IDS, toIds });
  }, []);

  const updateEditorState = useCallback((editorState) => {
    dispatch({ type: UPDATE_EDITOR_STATE, editorState });
  }, []);

  const updateTask = (task) => {
    dispatch({ type: UPDATE_TASK, task });
  };

  const updateCompletionDate = (completionDate) => {
    dispatch({ type: UPDATE_COMPLETION_DATE, completionDate });
  };

  const addTo = (to) => {
    dispatch({ type: ADD_TO, to });
  };

  const removeTo = useCallback((to) => {
    dispatch({ type: REMOVE_TO, toId: to.id });
  }, []);

  // use effect to update task in reducer when the task changes

  const updateCompanyGoal = (companyGoal) => {
    dispatch({ type: UPDATE_COMPANY_GOAL, companyGoal });
  };

  const restoreData = () => {
    updateTask({ ...currentTask, ...restorableData });
    setRestorableData({});
  };

  const updateDescription = (description) => {
    dispatch({ type: UPDATE_DESCRIPTION, description });
  };

  const clearTask = () => {
    dispatch({ type: CLEAR_TASK });
  };

  // useEffect(() => {
  //   updateTask({ ...task });
  // }, [JSON.stringify(task || '')]);

  return {
    currentTask,
    updateDueDate,
    updateTeamId,
    updateToIds,
    updateEditorState,
    updateTask,
    updateCompletionDate,
    addTo,
    removeTo,
    updateCompanyGoal,
    restorableData,
    setRestorableData,
    restoreData,
    updateDescription,
    clearTask,
    // updateTextMacros,
    // deleteMentions
    // addTaggedEmployeesToOwners
  };
}
